/**
 * Which version of the campaign a visitor is looking at.
 *
 * Two exist, deliberately:
 *
 *   mockup — Or's review mockup, reproduced: white 72px header, a 1120px
 *            rail, card-based layout, his palette, and the navy gradient
 *            panel. Its own stylesheet and its own components; it shares
 *            no CSS with the other design.
 *   full   — everything the marketing site carries, which is what the pages
 *            grew into between 30 September and 5 October.
 *
 * MOCKUP IS THE DEFAULT (decided 6 October). A bare `/c/journalist` serves
 * it; `?v=full` reaches the other. It replaced the short-lived `plain`
 * variant, which was an attempt at the same thing this does properly — a
 * stored `plain` therefore falls through to the default.
 *
 * The choice is STICKY for the visit. A visitor who arrives on `?v=full` and
 * then opens FAQ or Pricing must not silently fall back to plain: a journey
 * that changes shape underneath someone is worse than either version.
 */

const KEY = 'alchemiq.campaign.variant';

// `v3` is the Oct 2026 design package's landing page. It is reachable on
// `?v=v3` for the two audiences that already have a live page, and is the
// only thing `newsroom` can render. See landing/CampaignRoute.jsx.
export const VARIANTS = Object.freeze(['mockup', 'full', 'v3']);
export const DEFAULT_VARIANT = 'mockup';

function read() {
  try {
    const v = window.localStorage.getItem(KEY);
    return VARIANTS.includes(v) ? v : null;
  } catch {
    return null;
  }
}

function write(variant) {
  try {
    window.localStorage.setItem(KEY, variant);
  } catch {
    /* Private mode. The variant still applies for this render. */
  }
}

/**
 * `?v=` wins and is remembered; otherwise the remembered value; otherwise
 * the default. Unknown values are ignored rather than treated as `full`, so
 * a typo in an ad URL degrades to the default rather than to the other
 * version.
 */
export function resolveVariant(search) {
  let fromUrl = null;
  try {
    const q = new URLSearchParams(
      search !== undefined ? search : (typeof window === 'undefined' ? '' : window.location.search),
    );
    const raw = (q.get('v') || '').toLowerCase().trim();
    if (VARIANTS.includes(raw)) fromUrl = raw;
  } catch {
    /* A malformed query string is not worth failing the page over. */
  }

  if (fromUrl) {
    write(fromUrl);
    return fromUrl;
  }
  return read() || DEFAULT_VARIANT;
}

export function isMockup(variant) {
  // v3 never reaches the old component tree, but be explicit: treating it
  // as 'mockup' here would mean a stored 'v3' silently restyled the legacy
  // page for anyone who had visited the new one.
  return variant !== 'full' && variant !== 'v3';
}
